import createClient, { Client } from "openapi-fetch";
import type { components, paths } from "@opstel/gen/api/v2"; // generated by openapi-typescript
import { KnopReactApp } from "@opstel/reactApp/reactApp";
import { CorrelationId } from "@shared/telemetry/correlationId";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId";

export type RawAction = components["schemas"]["BaseAction"];
export type RawAllowedActionMutations = components["schemas"]["AllowedActionMutations"];
export type UpdateAction = components["schemas"]["UpdateAction"];
export type InstanceCreated = components["schemas"]["CreateInstanceResponse"];
export type CreateInstanceRequest = components["schemas"]["CreateInstanceRequest"];
export type AboutInfo = components["schemas"]["AboutInfo"];
export type BannerMessageResponse = components["schemas"]["BannerMessageResponse"];
export type RawBorderTrainsResponse = components["schemas"]["BorderTrainsResponse"];
export type RawScenarioBorderSelection = components["schemas"]["ScenarioBorderSelection"];
export type RawScenarioBorderTrain = components["schemas"]["ScenarioBorderTrain"];

function createCorrelationIdChildHeaders() {
  const correlationId = useCurrentInstanceId.getState().correlationId;
  if (!correlationId) {
    console.warn("Expected a correlation id but none was found, creating a new one");
    return createNewCorrelationIdHeaders();
  }
  return CorrelationId.toHeaders(CorrelationId.createChild(correlationId));
}

function createNewCorrelationIdHeaders() {
  return CorrelationId.toHeaders(CorrelationId.create());
}

function storeCorrelationId({
  correlationOperationId,
  correlationParentId,
  correlationCurrentId,
}: InstanceCreated) {
  if (!correlationOperationId || !correlationParentId || !correlationCurrentId) {
    console.warn("Instance was created without a correlation id");
    return;
  }
  useCurrentInstanceId.getState().setCorrelationId(
    CorrelationId.fromProto({
      correlationOperationId,
      correlationParentId,
      correlationCurrentId,
    }),
  );
}

export class OpstelApiClient {
  private readonly client: Client<paths>;

  constructor(baseUrl: string, knopReactApp: KnopReactApp) {
    this.client = createClient<paths>({ baseUrl });
    this.client.use({
      async onRequest({ request }) {
        const token = await knopReactApp.acquireToken();
        request.headers.set("Authorization", `Bearer ${token}`);
        return request;
      },
    });
  }

  async getActions(
    pathParams: paths["/instances/{instanceId}/actions"]["get"]["parameters"]["path"],
  ): Promise<RawAction[]> {
    const { data, error } = await this.client.GET("/instances/{instanceId}/actions", {
      params: { path: pathParams },
      headers: createCorrelationIdChildHeaders(),
    });
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }

  async getActionsForShuntingUnit(
    pathParams: paths["/instances/{instanceId}/shuntingUnits/{shuntingUnitId}/actions"]["get"]["parameters"]["path"],
  ): Promise<RawAction[]> {
    const { data, error } = await this.client.GET(
      "/instances/{instanceId}/shuntingUnits/{shuntingUnitId}/actions",
      {
        params: { path: pathParams },
        headers: createCorrelationIdChildHeaders(),
      },
    );
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }

  async getAction(
    pathParams: paths["/instances/{instanceId}/actions/{actionId}"]["get"]["parameters"]["path"],
  ): Promise<RawAction> {
    const { data, error } = await this.client.GET("/instances/{instanceId}/actions/{actionId}", {
      params: { path: pathParams },
      headers: createCorrelationIdChildHeaders(),
    });
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }

  async getAllowedActionMutations(
    pathParams: paths["/instances/{instanceId}/actions/{actionId}/allowedMutations"]["get"]["parameters"]["path"],
  ): Promise<RawAllowedActionMutations> {
    const { data, error } = await this.client.GET(
      "/instances/{instanceId}/actions/{actionId}/allowedMutations",
      {
        params: { path: pathParams },
        headers: createCorrelationIdChildHeaders(),
      },
    );
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }

  async updateAction(
    pathParams: paths["/instances/{instanceId}/actions/{actionId}"]["patch"]["parameters"]["path"],
    request: UpdateAction,
  ): Promise<void> {
    const { error } = await this.client.PATCH("/instances/{instanceId}/actions/{actionId}", {
      params: { path: pathParams },
      body: request,
      headers: createCorrelationIdChildHeaders(),
    });
    if (error) {
      throw new Error(error);
    }
  }

  async createInstance(request: CreateInstanceRequest): Promise<InstanceCreated> {
    const { data, error } = await this.client.POST("/instances", {
      body: request,
      headers: createNewCorrelationIdHeaders(),
    });

    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }

    storeCorrelationId(data);
    return data;
  }

  async getBorderTrains(
    pathParams: paths["/instances/{instanceId}/shuntingUnits/{shuntingUnitId}/borderTrains"]["get"]["parameters"]["path"],
  ): Promise<RawBorderTrainsResponse> {
    const { data, error } = await this.client.GET(
      "/instances/{instanceId}/shuntingUnits/{shuntingUnitId}/borderTrains",
      {
        params: { path: pathParams },
        headers: createCorrelationIdChildHeaders(),
      },
    );
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }

  async getAboutInfo(): Promise<AboutInfo> {
    const { data, error } = await this.client.GET("/about", {
      headers: createNewCorrelationIdHeaders(),
    });
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }

  async getBannerMessage(): Promise<BannerMessageResponse> {
    const { data, error } = await this.client.GET("/bannerMessage", {
      headers: createNewCorrelationIdHeaders(),
    });
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }

  async getReleaseNotes(): Promise<string> {
    const { data, error } = await this.client.GET("/releaseNotes", {
      parseAs: "text",
      headers: createNewCorrelationIdHeaders(),
    });
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }

  async getConflicts(
    pathParams: paths["/instances/{instanceId}/conflicts"]["get"]["parameters"]["path"],
  ): Promise<components["schemas"]["Conflict"][]> {
    const { data, error } = await this.client.GET("/instances/{instanceId}/conflicts", {
      params: { path: pathParams },
      headers: createCorrelationIdChildHeaders(),
    });
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }

  async getServiceTasks(
    pathParams: paths["/instances/{instanceId}/serviceTasks"]["get"]["parameters"]["path"],
  ): Promise<components["schemas"]["ServiceTask"][]> {
    const { data, error } = await this.client.GET("/instances/{instanceId}/serviceTasks", {
      params: { path: pathParams },
      headers: createCorrelationIdChildHeaders(),
    });
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }

  async getAllScenarioBorderSelections(
    pathParams: paths["/instances/{instanceId}/scenarioBorderSelections"]["get"]["parameters"]["path"],
  ): Promise<RawScenarioBorderSelection[]> {
    const { data, error } = await this.client.GET(
      "/instances/{instanceId}/scenarioBorderSelections",
      {
        params: { path: pathParams },
        headers: createCorrelationIdChildHeaders(),
      },
    );
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }

  async updateScenarioBorderSelections(
    pathParams: paths["/instances/{instanceId}/scenarioBorderSelections"]["patch"]["parameters"]["path"],
    request: RawScenarioBorderSelection[],
  ): Promise<void> {
    const { error } = await this.client.PATCH("/instances/{instanceId}/scenarioBorderSelections", {
      params: { path: pathParams },
      body: request,
      headers: createCorrelationIdChildHeaders(),
    });
    if (error) {
      throw new Error(error);
    }
  }

  async getAllAppearingScenarioBorderTrains(
    pathParams: paths["/instances/{instanceId}/appearingScenarioBorderTrains"]["get"]["parameters"]["path"],
  ): Promise<RawScenarioBorderTrain[]> {
    const { data, error } = await this.client.GET(
      "/instances/{instanceId}/appearingScenarioBorderTrains",
      {
        params: { path: pathParams },
        headers: createCorrelationIdChildHeaders(),
      },
    );
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }

  async getAllDisappearingScenarioBorderTrains(
    pathParams: paths["/instances/{instanceId}/disappearingScenarioBorderTrains"]["get"]["parameters"]["path"],
  ): Promise<RawScenarioBorderTrain[]> {
    const { data, error } = await this.client.GET(
      "/instances/{instanceId}/disappearingScenarioBorderTrains",
      {
        params: { path: pathParams },
        headers: createCorrelationIdChildHeaders(),
      },
    );
    if (error) {
      throw new Error(error);
    }
    if (!data) {
      throw new Error("Data is undefined");
    }
    return data;
  }
}
